import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { Building2, Calendar, MessageSquare, IndianRupee, ArrowUpRight } from "lucide-react";

const statCards = [
  { key: "total_tenants", label: "Total Tenants", icon: Building2 },
  { key: "active_tenants", label: "Active Tenants", icon: Building2 },
  { key: "total_bookings", label: "Total Bookings", icon: Calendar },
  { key: "total_enquiries", label: "Enquiries", icon: MessageSquare },
];

export default function SuperDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/super/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-sm text-neutral-500">Loading…</div>;
  const { stats, recent_tenants, by_tenant } = data;

  return (
    <div data-testid="super-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <div className="admin-overline">Overview</div>
          <h1 className="admin-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">Platform pulse.</h1>
        </div>
        <div className="sm:text-right">
          <div className="admin-overline">Lifetime GMV</div>
          <div className="admin-heading text-3xl font-bold flex items-center gap-1 mt-1">
            <IndianRupee className="h-5 w-5" />
            {Number(stats.gmv || 0).toLocaleString("en-IN")}
          </div>
        </div>
      </div>

      <div className="stat-grid">
        {statCards.map((c) => (
          <div key={c.key} data-testid={`super-stat-${c.key}`}>
            <c.icon className="h-4 w-4 text-neutral-400 mb-3" />
            <div className="admin-overline">{c.label}</div>
            <div className="admin-heading text-3xl font-bold mt-1">{stats[c.key] ?? 0}</div>
          </div>
        ))}
        <div>
          <div className="admin-overline">Pending</div>
          <div className="admin-heading text-3xl font-bold mt-1">{stats.pending_tenants}</div>
        </div>
        <div>
          <div className="admin-overline">Suspended</div>
          <div className="admin-heading text-3xl font-bold mt-1">{stats.suspended_tenants}</div>
        </div>
        <div>
          <div className="admin-overline">Plans</div>
          <div className="admin-heading text-3xl font-bold mt-1">{stats.total_plans}</div>
        </div>
        <div>
          <div className="admin-overline">Active rate</div>
          <div className="admin-heading text-3xl font-bold mt-1">
            {stats.total_tenants ? Math.round((stats.active_tenants / stats.total_tenants) * 100) : 0}%
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 mt-6 sm:mt-8">
        <div className="bg-white border border-neutral-200 p-4 sm:p-6 min-w-0">
          <div className="admin-overline mb-4">Recent tenants</div>
          <div className="space-y-3">
            {recent_tenants.length === 0 && <div className="text-sm text-neutral-500">No tenants yet</div>}
            {recent_tenants.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 pb-3">
                <div className="min-w-0">
                  <div className="font-semibold text-sm break-words">{t.business_name}</div>
                  <div className="text-xs text-neutral-500 mt-0.5 break-anywhere">/{t.slug} · {t.plan_code}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="status-pill" style={{ background: t.status === "active" ? "#DCFCE7" : "#FEF3C7", color: t.status === "active" ? "#166534" : "#854D0E" }}>{t.status}</span>
                  <Link to={`/t/${t.slug}`} className="text-xs text-black underline-offset-4 hover:underline inline-flex items-center gap-1" data-testid={`super-recent-tenant-link-${t.slug}`}>
                    Open <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-neutral-200 p-4 sm:p-6 min-w-0">
          <div className="admin-overline mb-4">By tenant</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-neutral-500 border-b border-neutral-200">
              <th className="py-2">Tenant</th><th>Bookings</th><th>Enquiries</th>
            </tr></thead>
            <tbody>
              {by_tenant.map((row) => (
                <tr key={row.slug} className="border-b border-neutral-100">
                  <td className="py-2 font-medium">{row.tenant}</td>
                  <td>{row.bookings}</td>
                  <td>{row.enquiries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
